/**
 * Blog Type Definitions — Authoritative reference for all blog content types.
 *
 * Each blog type declares:
 *   - metadata (key, label i18n key, icon, description)
 *   - required/optional fields (what the author must provide)
 *   - editor features (which TipTap extensions are enabled)
 *   - template constraints (structural rules the editor enforces)
 *   - review tier (which AI and human review pipelines apply)
 *
 * Adding a new blog type:
 *   1. Add a value to BlogTypeKey
 *   2. Add a BlogTypeDefinition to BLOG_TYPES
 *   3. Add locale keys under userProfile.knowledge.blogTypes.<key>.*
 *   4. (Phase 3) Add AI review rules if the type needs them
 *
 * Phase 2 TODO: Wire submission to NewsController via contentType = 'user_blog'
 * Phase 3 TODO: AI review pipeline (political bias, scientific accuracy,
 *               truthfulness, community standards). After clean AI return,
 *               route to human reviewer via existing news moderation queue.
 * Future TODO:  Wire blog publication events to the Impact Signature merit
 *               engine — published blogs earn contribution points on the
 *               "Voice" dimension (see ImpactSignatureMini.tsx).
 */

import i18n from '../../i18n/config';

const t = i18n.t.bind(i18n) as (key: string) => string;

// ── Blog type keys ──────────────────────────────────────────────

export const BLOG_TYPE_KEYS = [
  'professional_news',
  'opinion',
  'personal',
  'scholarly',
  'how_to',
  'best_practice',
  'lesson_learned',
] as const;

export type BlogTypeKey = (typeof BLOG_TYPE_KEYS)[number];

// ── Editor feature flags ────────────────────────────────────────

export interface EditorFeatures {
  /** Rich text basics: bold, italic, underline, headings */
  richText: boolean;
  /** Ordered & unordered lists */
  lists: boolean;
  /** Inline and block quotes */
  blockquote: boolean;
  /** Hyperlinks */
  links: boolean;
  /** Inline images / hero image upload */
  images: boolean;
  /** Footnotes / endnotes (Phase 1: scholarly only, future: all) */
  footnotes: boolean;
  /** Structured citations / bibliography */
  citations: boolean;
  /** Pull quotes / callout blocks */
  pullQuotes: boolean;
  /** Data tables (rows, columns, headers) */
  tables: boolean;
  /** Dateline field (city, date) for news articles */
  dateline: boolean;
  /** Source attribution / "Sources:" section */
  sourceAttribution: boolean;
}

// ── Template field requirements ─────────────────────────────────

export type FieldRequirement = 'required' | 'optional' | 'hidden';

export interface TemplateFields {
  headline: FieldRequirement;
  subtitle: FieldRequirement;
  byline: FieldRequirement;
  dateline: FieldRequirement;
  lede: FieldRequirement;           // opening summary paragraph
  abstract: FieldRequirement;       // scholarly abstract (structured summary)
  body: FieldRequirement;
  heroImage: FieldRequirement;
  tags: FieldRequirement;
  keywords: FieldRequirement;       // academic keywords (distinct from tags)
  sources: FieldRequirement;        // source attribution block
  bibliography: FieldRequirement;   // formal bibliography / works cited
  footnotes: FieldRequirement;
  category: FieldRequirement;
  opinionDisclaimer: FieldRequirement;  // mandatory "OPINION" label
  methodology: FieldRequirement;    // research methodology section
  acknowledgments: FieldRequirement; // funding / contributor acknowledgments

  // ── Knowledge blog fields (How-To, Best Practice, Lesson Learned) ──
  steps: FieldRequirement;              // ordered steps for How-To
  checklist: FieldRequirement;          // materials/prerequisites checklist
  difficulty: FieldRequirement;         // difficulty/skill level indicator
  timeEstimate: FieldRequirement;       // estimated time to complete
  caseReferences: FieldRequirement;     // linked case studies / evidence
  beforeAfter: FieldRequirement;        // before/after comparison section
  projectAssociation: FieldRequirement; // link to L4E project
  phaseContext: FieldRequirement;       // which project phase this relates to
  outcomeDescription: FieldRequirement; // what actually happened
  recommendations: FieldRequirement;    // actionable takeaways
  dimensionTags: FieldRequirement;      // Impact Signature dimension tags

  // ── How-To structured fields ──────────────────────────────────────
  materialsChecklist: FieldRequirement;    // editable list of tools & materials needed
                                           //   each item: { item, quantity?, unit?, optional? }
  processSteps: FieldRequirement;          // ordered process steps with QA checkpoints
                                           //   each: { instruction, tip?, qaCheck?, imageKey? }
  estimatedCost: FieldRequirement;         // cost estimate { amount?, currency?, notes? }
  estimatedDuration: FieldRequirement;     // duration to perform { value?, unit?, notes? }
  yield: FieldRequirement;                 // expected outcome / what success looks like
  prerequisites: FieldRequirement;         // skills, certifications, prior knowledge needed
  safetyWarnings: FieldRequirement;        // PPE, hazards, allergens, cautions
  variations: FieldRequirement;            // substitutions / alternate approaches
                                           //   each: { original, substitute, notes? }
  scaleNotes: FieldRequirement;            // how to scale up/down (serves 4 → serves 12)

  // ── Best Practice structured fields ─────────────────────────────────
  evidenceMetrics: FieldRequirement;       // quantitative evidence proving the practice works
                                           //   each: { metric, value, source?, notes? }
  applicabilityContext: FieldRequirement;  // when/where/under what conditions this applies
                                           //   { geography?, scale?, sector?, climate?, notes? }
  successConditions: FieldRequirement;     // conditions that must hold for this to succeed
  failureConditions: FieldRequirement;     // conditions / anti-patterns that cause failure
  adoptionDifficulty: FieldRequirement;    // how hard it is to adopt: easy | moderate | hard | expert
  timeToResults: FieldRequirement;         // how long until you see measurable outcomes
                                           //   { value?, unit?, notes? }
  costBenefitSummary: FieldRequirement;    // high-level cost vs. benefit narrative

  // ── Lesson Learned structured fields ────────────────────────────────
  expectedOutcome: FieldRequirement;       // what was supposed to happen
  actualOutcome: FieldRequirement;         // what actually happened
  rootCauseAnalysis: FieldRequirement;     // why the gap between expected and actual
  whatWorked: FieldRequirement;            // things that went right (keep doing these)
  whatDidNotWork: FieldRequirement;        // things that went wrong (stop/change these)
  audienceTags: FieldRequirement;          // who should read this (roles, domains)
  severityRating: FieldRequirement;        // impact severity: low | moderate | high | critical
  discoveryTimeline: FieldRequirement;     // when / how the lesson was discovered
                                           //   { discoveredAt?, phase?, trigger?, notes? }
}

// ── AI review tier (Phase 3) ────────────────────────────────────

export type ReviewTier = 'standard' | 'editorial' | 'scholarly';

// ── Blog type definition ────────────────────────────────────────

export interface BlogTypeDefinition {
  key: BlogTypeKey;
  /** i18n key prefix: userProfile.knowledge.blogTypes.<key> */
  i18nPrefix: string;
  /** Emoji or icon identifier for UI display */
  icon: string;
  /** Which editor extensions to enable */
  editorFeatures: EditorFeatures;
  /** Which template fields are required/optional/hidden */
  templateFields: TemplateFields;
  /** AI review pipeline tier (Phase 3) */
  reviewTier: ReviewTier;
  /** Maps to news system content_type for Phase 2 submission */
  newsContentType: 'user_blog';
  /** Minimum word count for body (0 = no minimum) */
  minWordCount: number;
  /** Maximum word count (0 = unlimited) */
  maxWordCount: number;
}

// ── Type definitions ────────────────────────────────────────────

export const BLOG_TYPES: Record<BlogTypeKey, BlogTypeDefinition> = {

  /**
   * Professional News — Follows journalistic standards.
   *
   * Structure: Inverted pyramid (most important info first).
   * Requirements: Headline, byline, dateline, lede paragraph, sources.
   * Tone: Neutral, factual, third-person. No first-person opinion.
   * Standards reference: AP Stylebook, SPJ Code of Ethics.
   */
  professional_news: {
    key: 'professional_news',
    i18nPrefix: 'userProfile:knowledge.blogTypes.professionalNews',
    icon: '📰',
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: true,
      tables: false,
      dateline: true,
      sourceAttribution: true,
    },
    templateFields: {
      headline: 'required',
      subtitle: 'optional',
      byline: 'required',
      dateline: 'required',
      lede: 'required',
      abstract: 'hidden',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      keywords: 'hidden',
      sources: 'required',
      bibliography: 'hidden',
      footnotes: 'hidden',
      category: 'required',
      opinionDisclaimer: 'hidden',
      methodology: 'hidden',
      acknowledgments: 'hidden',
      steps: 'hidden',
      checklist: 'hidden',
      difficulty: 'hidden',
      timeEstimate: 'hidden',
      caseReferences: 'hidden',
      beforeAfter: 'hidden',
      projectAssociation: 'hidden',
      phaseContext: 'hidden',
      outcomeDescription: 'hidden',
      recommendations: 'hidden',
      dimensionTags: 'hidden',
      materialsChecklist: 'hidden',
      processSteps: 'hidden',
      estimatedCost: 'hidden',
      estimatedDuration: 'hidden',
      yield: 'hidden',
      prerequisites: 'hidden',
      safetyWarnings: 'hidden',
      variations: 'hidden',
      scaleNotes: 'hidden',
      evidenceMetrics: 'hidden',
      applicabilityContext: 'hidden',
      successConditions: 'hidden',
      failureConditions: 'hidden',
      adoptionDifficulty: 'hidden',
      timeToResults: 'hidden',
      costBenefitSummary: 'hidden',
      expectedOutcome: 'hidden',
      actualOutcome: 'hidden',
      rootCauseAnalysis: 'hidden',
      whatWorked: 'hidden',
      whatDidNotWork: 'hidden',
      audienceTags: 'hidden',
      severityRating: 'hidden',
      discoveryTimeline: 'hidden',
    },
    reviewTier: 'editorial',
    newsContentType: 'user_blog',
    minWordCount: 200,
    maxWordCount: 5000,
  },

  /**
   * Opinion / News Lite — Personal perspective on news topics.
   *
   * Same editor capabilities as professional news, but:
   *   - "OPINION" badge is mandatory and non-removable
   *   - First-person voice is expected
   *   - Source requirements are relaxed (optional, not required)
   *   - Dateline is optional
   *
   * The opinion disclaimer appears prominently at the top of the
   * published article and cannot be removed by the author.
   */
  opinion: {
    key: 'opinion',
    i18nPrefix: 'userProfile:knowledge.blogTypes.opinion',
    icon: '💬',
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: true,
      tables: false,
      dateline: false,
      sourceAttribution: true,
    },
    templateFields: {
      headline: 'required',
      subtitle: 'optional',
      byline: 'required',
      dateline: 'optional',
      lede: 'optional',
      abstract: 'hidden',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      keywords: 'hidden',
      sources: 'optional',
      bibliography: 'hidden',
      footnotes: 'hidden',
      category: 'optional',
      opinionDisclaimer: 'required',   // ← non-removable "OPINION" label
      methodology: 'hidden',
      acknowledgments: 'hidden',
      steps: 'hidden',
      checklist: 'hidden',
      difficulty: 'hidden',
      timeEstimate: 'hidden',
      caseReferences: 'hidden',
      beforeAfter: 'hidden',
      projectAssociation: 'hidden',
      phaseContext: 'hidden',
      outcomeDescription: 'hidden',
      recommendations: 'hidden',
      dimensionTags: 'hidden',
      materialsChecklist: 'hidden',
      processSteps: 'hidden',
      estimatedCost: 'hidden',
      estimatedDuration: 'hidden',
      yield: 'hidden',
      prerequisites: 'hidden',
      safetyWarnings: 'hidden',
      variations: 'hidden',
      scaleNotes: 'hidden',
      evidenceMetrics: 'hidden',
      applicabilityContext: 'hidden',
      successConditions: 'hidden',
      failureConditions: 'hidden',
      adoptionDifficulty: 'hidden',
      timeToResults: 'hidden',
      costBenefitSummary: 'hidden',
      expectedOutcome: 'hidden',
      actualOutcome: 'hidden',
      rootCauseAnalysis: 'hidden',
      whatWorked: 'hidden',
      whatDidNotWork: 'hidden',
      audienceTags: 'hidden',
      severityRating: 'hidden',
      discoveryTimeline: 'hidden',
    },
    reviewTier: 'standard',
    newsContentType: 'user_blog',
    minWordCount: 100,
    maxWordCount: 3000,
  },

  /**
   * Personal Blog — Freeform expression.
   *
   * No structural enforcement. Title + body, everything else optional.
   * Casual voice, personal stories, reflections, project updates.
   * Lightest review requirements.
   */
  personal: {
    key: 'personal',
    i18nPrefix: 'userProfile:knowledge.blogTypes.personal',
    icon: '✍️',
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: false,
      tables: false,
      dateline: false,
      sourceAttribution: false,
    },
    templateFields: {
      headline: 'required',
      subtitle: 'optional',
      byline: 'hidden',          // auto-filled from user profile
      dateline: 'hidden',
      lede: 'hidden',
      abstract: 'hidden',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      keywords: 'hidden',
      sources: 'hidden',
      bibliography: 'hidden',
      footnotes: 'hidden',
      category: 'optional',
      opinionDisclaimer: 'hidden',
      methodology: 'hidden',
      acknowledgments: 'hidden',
      steps: 'hidden',
      checklist: 'hidden',
      difficulty: 'hidden',
      timeEstimate: 'hidden',
      caseReferences: 'hidden',
      beforeAfter: 'hidden',
      projectAssociation: 'hidden',
      phaseContext: 'hidden',
      outcomeDescription: 'hidden',
      recommendations: 'hidden',
      dimensionTags: 'hidden',
      materialsChecklist: 'hidden',
      processSteps: 'hidden',
      estimatedCost: 'hidden',
      estimatedDuration: 'hidden',
      yield: 'hidden',
      prerequisites: 'hidden',
      safetyWarnings: 'hidden',
      variations: 'hidden',
      scaleNotes: 'hidden',
      evidenceMetrics: 'hidden',
      applicabilityContext: 'hidden',
      successConditions: 'hidden',
      failureConditions: 'hidden',
      adoptionDifficulty: 'hidden',
      timeToResults: 'hidden',
      costBenefitSummary: 'hidden',
      expectedOutcome: 'hidden',
      actualOutcome: 'hidden',
      rootCauseAnalysis: 'hidden',
      whatWorked: 'hidden',
      whatDidNotWork: 'hidden',
      audienceTags: 'hidden',
      severityRating: 'hidden',
      discoveryTimeline: 'hidden',
    },
    reviewTier: 'standard',
    newsContentType: 'user_blog',
    minWordCount: 0,
    maxWordCount: 0,              // unlimited
  },

  /**
   * Scholarly Article — Academic / research paper format.
   *
   * Structure: Abstract → Introduction → Methodology → Results → Discussion → Conclusion
   * Requirements: Title, abstract, body, bibliography. Footnotes and data tables enabled.
   * Tone: Formal, evidence-based, third-person. Citations required.
   * Standards reference: APA 7th Edition, Chicago Manual of Style.
   *
   * Special features:
   *   - Abstract field (structured summary, 150–300 words)
   *   - Footnotes/endnotes with superscript numbering
   *   - Data tables (insertable via toolbar)
   *   - Bibliography/works cited section (formal references)
   *   - Academic keywords (separate from tags)
   *   - Methodology section
   *   - Acknowledgments (funding, contributors)
   */
  scholarly: {
    key: 'scholarly',
    i18nPrefix: 'userProfile:knowledge.blogTypes.scholarly',
    icon: '🎓',
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: true,
      citations: true,
      pullQuotes: false,
      tables: true,
      dateline: false,
      sourceAttribution: false,     // uses bibliography instead
    },
    templateFields: {
      headline: 'required',         // paper title
      subtitle: 'optional',         // subtitle / running head
      byline: 'required',           // author(s)
      dateline: 'hidden',
      lede: 'hidden',
      abstract: 'required',         // 150–300 word structured summary
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      keywords: 'required',         // academic keywords (3–6)
      sources: 'hidden',            // uses bibliography instead
      bibliography: 'required',     // formal works cited
      footnotes: 'required',        // enabled in editor
      category: 'required',         // discipline / field
      opinionDisclaimer: 'hidden',
      methodology: 'optional',      // research methodology section
      acknowledgments: 'optional',  // funding / contributor acknowledgments
      steps: 'hidden',
      checklist: 'hidden',
      difficulty: 'hidden',
      timeEstimate: 'hidden',
      caseReferences: 'hidden',
      beforeAfter: 'hidden',
      projectAssociation: 'hidden',
      phaseContext: 'hidden',
      outcomeDescription: 'hidden',
      recommendations: 'hidden',
      dimensionTags: 'hidden',
      materialsChecklist: 'hidden',
      processSteps: 'hidden',
      estimatedCost: 'hidden',
      estimatedDuration: 'hidden',
      yield: 'hidden',
      prerequisites: 'hidden',
      safetyWarnings: 'hidden',
      variations: 'hidden',
      scaleNotes: 'hidden',
      evidenceMetrics: 'hidden',
      applicabilityContext: 'hidden',
      successConditions: 'hidden',
      failureConditions: 'hidden',
      adoptionDifficulty: 'hidden',
      timeToResults: 'hidden',
      costBenefitSummary: 'hidden',
      expectedOutcome: 'hidden',
      actualOutcome: 'hidden',
      rootCauseAnalysis: 'hidden',
      whatWorked: 'hidden',
      whatDidNotWork: 'hidden',
      audienceTags: 'hidden',
      severityRating: 'hidden',
      discoveryTimeline: 'hidden',
    },
    reviewTier: 'scholarly',
    newsContentType: 'user_blog',
    minWordCount: 1000,
    maxWordCount: 0,                // unlimited
  },

  // ══════════════════════════════════════════════════════════════
  // Knowledge Blog Types
  // ══════════════════════════════════════════════════════════════

  /**
   * How-To — Procedural instructions that someone else could follow.
   *
   * Structure: Introduction → Tools & Materials → Process Steps (with QA) → Tips → Summary
   * Almost like a mini-project: photos, videos, step-by-step with checklists.
   * Must be replicable by the reader. Emphasis on clarity and completeness.
   *
   * Recipe-compatible: By changing labels this template serves as a recipe blog.
   *   "Tools & Materials" → "Ingredients & Equipment"
   *   "Process Steps" → "Directions"
   *   "Yield" → "Serves / Makes"
   *   "Safety Warnings" → "Allergens & Cautions"
   *   "Variations" → "Substitutions"
   *
   * Special features:
   *   - Editable tools & materials checklist (item, qty, unit, optional flag)
   *   - Ordered process steps with embedded QA checkpoints + optional images
   *   - Estimated cost and duration
   *   - Yield / expected outcome description
   *   - Prerequisites (skills, certifications, prior knowledge)
   *   - Safety warnings / cautions (PPE, hazards, allergens)
   *   - Variations / substitutions list
   *   - Scale notes (how to adjust for larger/smaller scope)
   *   - Difficulty level indicator (Beginner / Intermediate / Advanced)
   *   - Before/after comparison
   *   - Dimension tags (links to Impact Signature)
   */
  how_to: {
    key: 'how_to',
    i18nPrefix: 'userProfile:knowledge.blogTypes.howTo',
    icon: '🔧',
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: false,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: false,
      tables: true,              // materials tables, comparison tables
      dateline: false,
      sourceAttribution: true,   // optional source/credit for methods
    },
    templateFields: {
      headline: 'required',        // "How to Build a Rain Garden"
      subtitle: 'optional',        // brief summary
      byline: 'hidden',           // auto-filled
      dateline: 'hidden',
      lede: 'optional',           // introductory context
      abstract: 'hidden',
      body: 'required',           // supplementary narrative / introduction
      heroImage: 'optional',      // cover image (final result photo)
      tags: 'optional',
      keywords: 'hidden',
      sources: 'optional',        // credit for methods
      bibliography: 'hidden',
      footnotes: 'hidden',
      category: 'required',       // project taxonomy category
      opinionDisclaimer: 'hidden',
      methodology: 'hidden',
      acknowledgments: 'optional',

      // ── Legacy fields (kept for backward compat, superseded by new fields) ──
      steps: 'hidden',            // superseded by processSteps
      checklist: 'hidden',        // superseded by materialsChecklist
      timeEstimate: 'hidden',     // superseded by estimatedDuration

      difficulty: 'required',     // beginner | intermediate | advanced
      caseReferences: 'hidden',
      beforeAfter: 'optional',    // before/after photos showing result
      projectAssociation: 'optional', // link to the L4E project this relates to
      phaseContext: 'hidden',
      outcomeDescription: 'hidden',
      recommendations: 'optional',   // tips and best practices
      dimensionTags: 'required',     // impact dimensions

      // ── Structured How-To fields ──
      materialsChecklist: 'required',  // tools & materials list (item, qty, unit, optional?)
      processSteps: 'required',        // ordered steps with QA checkpoints
      estimatedCost: 'optional',       // cost estimate (amount, currency, notes)
      estimatedDuration: 'required',   // time to perform (value, unit, notes)
      yield: 'optional',              // expected outcome / what success looks like
      prerequisites: 'optional',       // skills, certs, prior knowledge needed
      safetyWarnings: 'optional',      // PPE, hazards, allergens, cautions
      variations: 'optional',          // substitutions / alternate approaches
      scaleNotes: 'optional',          // how to scale up/down
      evidenceMetrics: 'hidden',
      applicabilityContext: 'hidden',
      successConditions: 'hidden',
      failureConditions: 'hidden',
      adoptionDifficulty: 'hidden',
      timeToResults: 'hidden',
      costBenefitSummary: 'hidden',
      expectedOutcome: 'hidden',
      actualOutcome: 'hidden',
      rootCauseAnalysis: 'hidden',
      whatWorked: 'hidden',
      whatDidNotWork: 'hidden',
      audienceTags: 'hidden',
      severityRating: 'hidden',
      discoveryTimeline: 'hidden',
    },
    reviewTier: 'standard',
    newsContentType: 'user_blog',
    minWordCount: 200,
    maxWordCount: 10000,
  },

  /**
   * Best Practice — Prescriptive guidance with supporting evidence.
   *
   * Structure: Problem Context → Recommended Approach → Evidence → Case References → Summary
   * "The right way to do X and why." More authoritative than a How-To:
   * not just steps, but WHY these steps, supported by data or experience.
   *
   * Special features:
   *   - Evidence metrics (quantitative proof: "reduced erosion 40%")
   *   - Applicability context (geography, scale, sector, climate)
   *   - Success conditions (what must be true for this to work)
   *   - Failure conditions / anti-patterns (what causes it to fail)
   *   - Adoption difficulty rating (easy → expert)
   *   - Time to results (how long until outcomes are visible)
   *   - Cost-benefit summary (high-level ROI narrative)
   *   - Case references (linked examples or evidence)
   *   - Before/after comparison section
   *   - Sources and evidence requirements
   *   - Dimension tags (links to Impact Signature)
   */
  best_practice: {
    key: 'best_practice',
    i18nPrefix: 'userProfile:knowledge.blogTypes.bestPractice',
    icon: '⭐',
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: true,           // evidence citations
      pullQuotes: true,          // highlight key recommendations
      tables: true,              // comparison tables, data summaries
      dateline: false,
      sourceAttribution: true,
    },
    templateFields: {
      headline: 'required',        // "Sustainable Irrigation for Arid Regions"
      subtitle: 'optional',
      byline: 'hidden',           // auto-filled
      dateline: 'hidden',
      lede: 'optional',           // problem context summary
      abstract: 'optional',       // executive summary
      body: 'required',           // main guidance narrative
      heroImage: 'optional',
      tags: 'optional',
      keywords: 'hidden',
      sources: 'required',        // evidence and supporting references
      bibliography: 'optional',   // formal references if needed
      footnotes: 'hidden',
      category: 'required',
      opinionDisclaimer: 'hidden',
      methodology: 'hidden',
      acknowledgments: 'optional',
      steps: 'hidden',
      checklist: 'hidden',
      difficulty: 'hidden',
      timeEstimate: 'hidden',
      caseReferences: 'required', // real-world examples/evidence
      beforeAfter: 'optional',   // before/after comparison
      projectAssociation: 'optional',
      phaseContext: 'hidden',
      outcomeDescription: 'hidden',
      recommendations: 'required', // actionable prescriptions
      dimensionTags: 'required',
      materialsChecklist: 'hidden',
      processSteps: 'hidden',
      estimatedCost: 'hidden',
      estimatedDuration: 'hidden',
      yield: 'hidden',
      prerequisites: 'hidden',
      safetyWarnings: 'hidden',
      variations: 'hidden',
      scaleNotes: 'hidden',

      // ── Structured Best Practice fields ──
      evidenceMetrics: 'required',       // quantitative evidence (metric, value, source)
      applicabilityContext: 'required',  // when/where this applies (geography, scale, sector)
      successConditions: 'required',     // what must be true for this to succeed
      failureConditions: 'optional',     // anti-patterns / conditions that cause failure
      adoptionDifficulty: 'required',    // easy | moderate | hard | expert
      timeToResults: 'optional',         // how long until measurable outcomes
      costBenefitSummary: 'optional',    // high-level cost vs. benefit narrative
      expectedOutcome: 'hidden',
      actualOutcome: 'hidden',
      rootCauseAnalysis: 'hidden',
      whatWorked: 'hidden',
      whatDidNotWork: 'hidden',
      audienceTags: 'optional',          // who should read this
      severityRating: 'hidden',
      discoveryTimeline: 'hidden',
    },
    reviewTier: 'editorial',
    newsContentType: 'user_blog',
    minWordCount: 500,
    maxWordCount: 8000,
  },

  /**
   * Lesson Learned — Reflective post tied to a specific project or event.
   *
   * Structure: Context → Expected vs. Actual → Root Cause → What Worked /
   *            What Didn't → Recommendations → Next Steps
   * "What we did, what went right/wrong, and what others should know."
   * Always tied to a real project experience. Emphasis on honesty and utility.
   *
   * Special features:
   *   - Expected outcome (what was supposed to happen)
   *   - Actual outcome (what really happened — the gap drives the lesson)
   *   - Root cause analysis (why the gap exists)
   *   - What worked / what didn't (structured reflection)
   *   - Audience tags (who should read this — roles, domains)
   *   - Severity rating (low → critical — how impactful was this lesson)
   *   - Discovery timeline (when/how the lesson was discovered)
   *   - Project association (required — this is about a specific experience)
   *   - Phase context (which project phase the lesson relates to)
   *   - Recommendations (what to do differently)
   *   - Dimension tags (links to Impact Signature)
   */
  lesson_learned: {
    key: 'lesson_learned',
    i18nPrefix: 'userProfile:knowledge.blogTypes.lessonLearned',
    icon: '💡',
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: true,          // highlight key takeaways
      tables: false,
      dateline: false,
      sourceAttribution: false,
    },
    templateFields: {
      headline: 'required',        // "Why Our Community Cleanup Failed — And What We Fixed"
      subtitle: 'optional',
      byline: 'hidden',           // auto-filled
      dateline: 'hidden',
      lede: 'optional',           // brief context
      abstract: 'hidden',
      body: 'required',           // detailed narrative
      heroImage: 'optional',
      tags: 'optional',
      keywords: 'hidden',
      sources: 'hidden',
      bibliography: 'hidden',
      footnotes: 'hidden',
      category: 'required',
      opinionDisclaimer: 'hidden',
      methodology: 'hidden',
      acknowledgments: 'optional',
      steps: 'hidden',
      checklist: 'hidden',
      difficulty: 'hidden',
      timeEstimate: 'hidden',
      caseReferences: 'hidden',
      beforeAfter: 'optional',       // before/after if relevant
      projectAssociation: 'required', // which project this lesson comes from
      phaseContext: 'optional',       // which phase (planning, execution, etc.)
      outcomeDescription: 'required', // what happened vs. expectations
      recommendations: 'required',    // what to do differently
      dimensionTags: 'required',
      materialsChecklist: 'hidden',
      processSteps: 'hidden',
      estimatedCost: 'hidden',
      estimatedDuration: 'hidden',
      yield: 'hidden',
      prerequisites: 'hidden',
      safetyWarnings: 'hidden',
      variations: 'hidden',
      scaleNotes: 'hidden',

      // ── Structured Lesson Learned fields ──
      evidenceMetrics: 'hidden',
      applicabilityContext: 'hidden',
      successConditions: 'hidden',
      failureConditions: 'hidden',
      adoptionDifficulty: 'hidden',
      timeToResults: 'hidden',
      costBenefitSummary: 'hidden',
      expectedOutcome: 'required',       // what was supposed to happen
      actualOutcome: 'required',         // what actually happened
      rootCauseAnalysis: 'required',     // why the gap between expected and actual
      whatWorked: 'required',            // things that went right
      whatDidNotWork: 'required',        // things that went wrong
      audienceTags: 'optional',          // who should read this (roles, domains)
      severityRating: 'required',        // low | moderate | high | critical
      discoveryTimeline: 'optional',     // when/how the lesson was discovered
    },
    reviewTier: 'standard',
    newsContentType: 'user_blog',
    minWordCount: 300,
    maxWordCount: 5000,
  },
};

// ── Helpers ─────────────────────────────────────────────────────

/** Get the localized display name for a blog type. */
export function localizedBlogTypeName(key: BlogTypeKey): string {
  return t(`userProfile:knowledge.blogTypes.${key}.name`);
}

/** Get the localized description for a blog type. */
export function localizedBlogTypeDescription(key: BlogTypeKey): string {
  return t(`userProfile:knowledge.blogTypes.${key}.description`);
}

/** Get all required field keys for a blog type. */
export function getRequiredFields(key: BlogTypeKey): (keyof TemplateFields)[] {
  const def = BLOG_TYPES[key];
  return (Object.entries(def.templateFields) as [keyof TemplateFields, FieldRequirement][])
    .filter(([, req]) => req === 'required')
    .map(([field]) => field);
}

/** Get all visible (required + optional) field keys for a blog type. */
export function getVisibleFields(key: BlogTypeKey): (keyof TemplateFields)[] {
  const def = BLOG_TYPES[key];
  return (Object.entries(def.templateFields) as [keyof TemplateFields, FieldRequirement][])
    .filter(([, req]) => req !== 'hidden')
    .map(([field]) => field);
}
